import { ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { useSubscribePopup } from "@/components/SubscribePopupContext";
import { trackCta } from "@/lib/analytics";

const HeroSection = () => {
  const { t, localePath, locale } = useLanguage();
  const { openPopup } = useSubscribePopup();

  const handleJoinCommunity = () => {
    trackCta({
      cta_id: "hero_join_community",
      cta_location: "hero",
      cta_label: t.hero.cta,
    });
    openPopup();
  };

  const handleMoreAbout = () => {
    trackCta({
      cta_id: "hero_more_about",
      cta_location: "hero",
      cta_label: t.hero.more,
      cta_destination: localePath("/about"),
    });
  };

  return (
    <section aria-label={locale === "cs" ? "Úvodní sekce" : "Hero section"} className="hero-section relative h-screen flex items-end justify-center overflow-hidden text-[hsl(40,20%,95%)]">
      <picture>
        <source
          media="(max-width: 768px)"
          type="image/webp"
          srcSet="/hero-bg-mobile-420.webp 420w, /hero-bg-mobile-540.webp 540w, /hero-bg-mobile.webp 828w"
          sizes="100vw"
        />
        <source
          type="image/webp"
          srcSet="/hero-bg-opt-1280.webp 1280w, /hero-bg-opt-1440.webp 1440w, /hero-bg-opt-1536.webp 1536w, /hero-bg-opt-1680.webp 1680w, /hero-bg-opt.webp 1920w"
          sizes="100vw"
        />
        <img
          src="/hero-bg-opt.jpg"
          alt=""
          role="presentation"
          fetchPriority="high"
          decoding="sync"
          loading="eager"
          sizes="100vw"
          width={1920}
          height={1080}
          // @ts-expect-error elementtiming is a valid HTML attr, not in React types yet
          elementtiming="hero-image"
          className="absolute inset-0 w-full h-full object-cover object-[center_15%] md:object-[center_20%]"
        />
      </picture>
      <div className="absolute inset-0 bg-hero-overlay" />

      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto pb-20 md:pb-24">
        <p className="text-sm md:text-base uppercase tracking-[0.3em] text-[hsla(40,20%,95%,0.85)] mb-6 animate-fade-in">
          {t.hero.subtitle}
        </p>
        <h1
          className="font-display text-6xl md:text-8xl lg:text-9xl leading-none mb-6 animate-fade-in [animation-delay:200ms]"
        >
          <span className="text-gradient">Tomáš</span><br /><span className="text-[hsl(40,20%,95%)]">Strachota</span>
          <span className="block mt-4 text-base md:text-lg font-sans font-normal tracking-[0.2em] uppercase text-[hsla(40,20%,95%,0.9)]">
            {locale === "cs" ? "Chovatel exotického ptactva" : "Exotic bird breeder"}
          </span>
        </h1>
        <p className="text-lg md:text-xl text-[hsla(40,20%,95%,0.95)] max-w-2xl mx-auto mb-10 animate-fade-in [animation-delay:400ms]">
          {t.hero.description}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in [animation-delay:600ms]">
          <button onClick={handleJoinCommunity} className="bg-gradient-primary text-[hsl(40,20%,95%)] px-8 py-4 rounded-sm text-base font-semibold uppercase tracking-wider hover:opacity-90 transition-opacity shadow-glow">
            {t.hero.cta}
          </button>
          <Link to={localePath("/about")} onClick={handleMoreAbout} className="border border-[hsla(40,20%,95%,0.3)] text-[hsl(40,20%,95%)] px-8 py-4 rounded-sm text-base font-semibold uppercase tracking-wider hover:bg-[hsla(40,20%,95%,0.1)] transition-colors">
            {t.hero.more}
          </Link>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-muted-foreground animate-bounce-composited">
        <ChevronDown size={28} />
      </div>
    </section>
  );
};

export default HeroSection;
